<script>
    //import Rollbar from "./lib/rollbar.js";
    import Reporter from "./lib/error.js";
    import { onMount } from 'svelte';

    // elements
    import { Router, Link, Route, navigate } from "svelte-routing";
    import Icon from 'svelte-awesome';
    import { asterisk } from 'svelte-awesome/icons';

    // actions
    import { onLogin, clear as clearSession, logout } from "./Auth.svelte";
    import { printDebug } from "./GraphQL.svelte";
    import Util from "./lib/util.js";

    // routes
    import Dashboard from "./routes/Dashboard.svelte";
    import Admin from "./routes/Admin.svelte";
    import Login from "./routes/Login.svelte";
    import Project from "./routes/Project.svelte";
    import ResetPassword from "./routes/ResetPassword.svelte";

    // modals
    import LoadingModal from "./components/LoadingModal.svelte";
    import ErrorModal from "./components/ErrorModal.svelte";

    // the main avriable that determines the routing.
    // the routing component renders based on this
    export let url = new URL(window.location).pathname;

    //
    // local variables
    //

    // fatal error
    let error;
    let networkError;
    let loaded = false;

    printDebug();

    $: if (url == "/" || url == "") {
        url = "/dashboard";
    }

    $: if (url.includes("/logout")) {
        clearSession();
        url = "";
    }

    // direct call that does not receive event
    function changeRoute(path) {
        var tmp = path || "/";
        console.log(`navigating to ${tmp}...`);
        navigate(tmp, { replace: true });

        // remove query string in inner URL variable
        // so we have no problems matching the routes
        url = tmp.split("?")[0];
    }

    // direct call that does not receive event arg
    function authenticate(redirectAfter) {
        clearSession();
        changeRoute("/login?target=" + (Util.isString(redirectAfter) ? redirectAfter : url));
    }

    // change route and re-render
    function handleNavigate(event) {
        console.log(`handling navigation event...`);
        console.log(event);
        changeRoute(event.detail);
    }

    // accepts data event from login form and rerender target url
    const handleLogin = handleNavigate; // alias

    function handleError(err) {
        // TODO
    }

    // graphql errors
    function handleNetworkError(evt) {
        Reporter.error(`[App] handling network error`, evt);

        // TODO: why are we not getting apollo network details?
        if (!evt.detail || !evt.detail.networkError) {
            console.warn("Error object has no network request details!");
            networkError = {
                    statusCode: "unknown",
                    statusText: evt.detail.message,
                    result: evt.detail
                }
            return;
        }

        switch (evt.detail.networkError.statusCode) {
            // bad request
            case 400: {
                networkError = {
                        statusCode: 400,
                        statusText: evt.detail.networkError.response.statusText,
                        result: evt.detail.networkError.result
                    }
                break;
            }
            default: {
                debugger;
            }
        }
    }

    // set the automatic login determination request.
    // if a token has already been stored, try get our profile
    // with it. if it succeeds, automatically redirect
    // to target url. If not, allow user to login manually.
    // TODO: move this to module
    // TODO: replace with apollojs middleware in GraphQL module
	let loginDeterminationRequest =
	    onLogin() // sets token to localstorage
	    .then(res => {
	        console.log("found valid session");

	        let meUser = res.data.me.user;

	        // identify this session as a user for logrocket debugging
	        if (LogRocket) {
                LogRocket.identify(meUser.id, {
                  name: `${meUser.firstName} ${meUser.lastName}`,
                  email: meUser.emailAddress,

                  // Add your own custom user variables here, ie:
                  token: res.data.me.token
                });
            }

	        if (url.includes("/login")) {
	            changeRoute("/dashboard");
	        }

	        // remove modal loading screen
	        loaded = true;
	    })
	    .catch(res => {
	        console.warn("Failed to query Me, deleting session");
	        console.warn(res);

	        loaded = true;

            // make exception for paths that should always be reachable
            // TODO: integrate this into the router or define as constants
	        if (!url.includes("/signup") && !url.includes("reset-password")) {
                authenticate();
            }
        });

    // listen to token mutations in localstorage (when we log out in different tab)
    // https://stackoverflow.com/questions/50767241/observe-localstorage-changes-in-js
    window.addEventListener('storage', function(e) {
        if (e.key == "token" && e.oldValue != e.newValue && !e.newValue) {
            authenticate();
        }
    });

    onMount(async () => {
        document.body.className = document.body.className.replace("bootstrap", "");
    });
</script>

<svelte:head>
    <link href='http://fonts.googleapis.com/css?family=Great+Vibes' rel='stylesheet' type='text/css'>
</svelte:head>

<Router bind:url>
    <!-- app bootloading spinner -->
    {#if loaded}
        <!-- for debugging and styling -->
        <Route path="/loading">
            <LoadingModal
                    show={true}
                    loaderBackground="#fff"
                    background="#fff"
                    content="#fff"
                    lightSpinner />
        </Route>

        <!-- TODO: having trouble passing token GET parameter from here -->
        <Route path="/reset-password" let:params>
            <ResetPassword/>
        </Route>

        <Route path="/dashboard">
            <Dashboard
                    on:auth={authenticate}
                    on:error={handleError}
                    on:networkError={handleNetworkError}
                    on:loaded=""
            />
        </Route>

        <Route path="/admin">
            <Admin
                    on:auth={authenticate}
                    on:error={handleError}
                    on:networkError={handleNetworkError}
                    on:loaded=""
            />
        </Route>

        <Route path="/p/:projectId/:activeTab" let:params>
            <Project
                    on:auth={authenticate}
                    on:error={handleError}
                    on:networkError={handleNetworkError}
                    on:loaded=""
                    activeTab={params.activeTab}
                    id={params.projectId}
            />
        </Route>

        <Route path="/p/:projectId" let:params>
            <Project
                    on:auth={authenticate}
                    on:error={handleError}
                    on:networkError={handleNetworkError}
                    on:loaded=""
                    id={params.projectId}
            />
        </Route>

    {:else}
        <LoadingModal
                show={!loaded}
                loaderBackground="#fff"
                content="#fff"
                lightSpinner />
    {/if}

    <Route path="/login">
        <Login on:ok={handleLogin}/>
    </Route>

    <Route path="/signup">
        <Login on:ok={handleLogin} isRegisterType/>
    </Route>

    <Route path="/logout">
        logging out...
    </Route>

    <Route path="*">
        <div class="page-404">
            <div class="code">404</div>
            <p>Page not found</p>
        </div>
    </Route>
</Router>

<!-- when errors occur specific to network requests, show model error dialog -->
{#if networkError}
    <ErrorModal model={networkError} />
{/if}

<style>
    :global(img.bootstrap) {display: none;}

    :global(.mdc-button--raised:not(:disabled), .mdc-button--unelevated:not(:disabled)) {
        background-color: var(--mdc-theme-primary, #107afe) !important;
        background-color: var(--mdc-theme-primary, #2CD533) !important;
    }

    .page-404 {
        text-align: center;
        color: white;
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
    }

    .page-404 .code {
        font-weight: bold;
        font-size: 60px;
    }

    .page-404 p {
        font-size: 30px;
    }
</style>